import mqtt from "mqtt";
import { useState, useEffect, useRef, useCallback } from "react";

/**
 * Hook para suscribirse a múltiples devices via MQTT
 * @param {number[]} deviceIds - Array de IDs de dispositivos a escuchar
 * @param {string} topicBase - Base del topic (default: "myindustrialdata")
 * @param {boolean} includeGraphTopic - Si debe suscribirse también a myindustrialdatagraph (default: false)
 * @param {number} projectId - ID del proyecto para suscribirse a variables calculadas (opcional)
 * @returns {object} - { data, graphData, customVarData, loaded, reconnecting, ... }
 */
export default function useMQTTMultiDevice(deviceIds, topicBase = "myindustrialdata", includeGraphTopic = false, projectId = null) {
    const [client, setClient] = useState(null);
    const [reconnecting, setReconnecting] = useState(false);
    const [loaded, setLoaded] = useState(false);
    // data es un objeto: { deviceId: [variables...], deviceId2: [variables...] }
    const [data, setData] = useState({});
    // graphData almacena los nuevos puntos para gráficos: { deviceId: [{variable_data_id, value, x}, ...] }
    const [graphData, setGraphData] = useState({});
    // customVarData almacena los nuevos puntos de variables calculadas: [{custom_variable_id, name, value, x}, ...]
    const [customVarData, setCustomVarData] = useState([]);
    const subscribedTopics = useRef(new Set());

    // Conectar al broker MQTT
    useEffect(() => {
        if (!deviceIds || deviceIds.length === 0) {
            console.log('⏳ MQTT Multi: No device IDs provided');
            return;
        }

        const brokerUrl = process.env.REACT_APP_MQTT_WS_URL;
        const username = process.env.REACT_APP_MQTT_USER;
        const password = process.env.REACT_APP_MQTT_PASSWORD;

        if (!brokerUrl) {
            console.error('❌ MQTT Multi: REACT_APP_MQTT_WS_URL not configured');
            return;
        }

        console.log('🔌 MQTT Multi: Connecting to', brokerUrl);
        const mqttClient = mqtt.connect(brokerUrl, {
            username: username,
            password: password,
            clientId: `web_dashboard_${Math.random().toString(16).substring(2, 10)}`,
            clean: true,
            reconnectPeriod: 5000,
            connectTimeout: 30000,
        });

        setClient(mqttClient);

        return () => {
            if (mqttClient) {
                console.log('🔌 MQTT Multi: Disconnecting client');
                mqttClient.end();
            }
        };
    }, [deviceIds?.length > 0]); // Solo reconectar si pasamos de 0 devices a tener devices

    // Manejar suscripciones y mensajes
    useEffect(() => {
        if (!client || !deviceIds || deviceIds.length === 0) {
            return;
        }

        const onConnect = () => {
            console.log('✅ MQTT Multi: Connected to broker');
            setReconnecting(false);
            setLoaded(true);

            // Suscribirse a todos los topics de devices
            deviceIds.forEach(deviceId => {
                const topic = `${topicBase}/${deviceId}`;
                if (!subscribedTopics.current.has(topic)) {
                    client.subscribe(topic, { qos: 1 }, (err) => {
                        if (err) {
                            console.error(`❌ MQTT Multi: Error subscribing to ${topic}`, err);
                        } else {
                            console.log(`✅ MQTT Multi: Subscribed to ${topic}`);
                            subscribedTopics.current.add(topic);
                        }
                    });
                }

                // Suscribirse también al topic de gráficos si está habilitado
                if (includeGraphTopic) {
                    const graphTopic = `myindustrialdatagraph/${deviceId}`;
                    if (!subscribedTopics.current.has(graphTopic)) {
                        client.subscribe(graphTopic, { qos: 1 }, (err) => {
                            if (err) {
                                console.error(`❌ MQTT Multi: Error subscribing to ${graphTopic}`, err);
                            } else {
                                console.log(`✅ MQTT Multi: Subscribed to graph topic ${graphTopic}`);
                                subscribedTopics.current.add(graphTopic);
                            }
                        });
                    }
                }
            });

            // Suscribirse al topic de variables calculadas si se proporciona projectId
            if (projectId) {
                const customVarTopic = `myindustrialdatacustom/${projectId}`;
                if (!subscribedTopics.current.has(customVarTopic)) {
                    client.subscribe(customVarTopic, { qos: 1 }, (err) => {
                        if (err) {
                            console.error(`❌ MQTT Multi: Error subscribing to ${customVarTopic}`, err);
                        } else {
                            console.log(`✅ MQTT Multi: Subscribed to custom variable topic ${customVarTopic}`);
                            subscribedTopics.current.add(customVarTopic);
                        }
                    });
                }
            }
        };

        const onMessage = (receivedTopic, message) => {
            console.log('📨 MQTT Multi: Message on', receivedTopic);

            try {
                const parsed = JSON.parse(message.toString());

                // Detectar el tipo de mensaje por el topic
                const isGraphMessage = receivedTopic.startsWith('myindustrialdatagraph/');
                const isCustomVarMessage = receivedTopic.startsWith('myindustrialdatacustom/');

                if (isCustomVarMessage) {
                    console.log(`🔢 MQTT Multi: Custom variable data:`, parsed);
                    // Acumular nuevos puntos de variables calculadas
                    // Soporta tanto un objeto individual como un array de cambios
                    const newPoints = Array.isArray(parsed) ? parsed : [parsed];
                    setCustomVarData(prevData => [...prevData, ...newPoints]);
                } else if (isGraphMessage) {
                    const deviceId = parseInt(receivedTopic.split('/').pop());
                    console.log(`📊 MQTT Multi: Graph data for device ${deviceId}:`, parsed);
                    // Acumular nuevos puntos de gráfico por variable_data_id
                    // Soporta tanto un objeto individual como un array de cambios
                    const newPoints = Array.isArray(parsed) ? parsed : [parsed];
                    setGraphData(prevGraphData => {
                        const deviceGraphData = prevGraphData[deviceId] || [];
                        return {
                            ...prevGraphData,
                            [deviceId]: [...deviceGraphData, ...newPoints]
                        };
                    });
                } else {
                    const deviceId = parseInt(receivedTopic.split('/').pop());
                    console.log(`✅ MQTT Multi: Data for device ${deviceId}:`, parsed);
                    setData(prevData => ({
                        ...prevData,
                        [deviceId]: parsed
                    }));
                }
            } catch (e) {
                console.error('❌ MQTT Multi: Error parsing message', e);
            }
        };

        const onError = (err) => {
            console.error('MQTT Multi: Connection error', err);
            setReconnecting(true);
        };

        const onClose = () => {
            console.log('MQTT Multi: Connection closed');
            setReconnecting(true);
        };

        const onReconnect = () => {
            console.log('MQTT Multi: Reconnecting...');
            setReconnecting(true);
            subscribedTopics.current.clear(); // Limpiar para re-suscribirse
        };

        client.on('connect', onConnect);
        client.on('message', onMessage);
        client.on('error', onError);
        client.on('close', onClose);
        client.on('reconnect', onReconnect);

        // Si ya está conectado, suscribirse inmediatamente
        if (client.connected) {
            onConnect();
        }

        return () => {
            client.off('connect', onConnect);
            client.off('message', onMessage);
            client.off('error', onError);
            client.off('close', onClose);
            client.off('reconnect', onReconnect);

            // Desuscribirse de todos los topics
            subscribedTopics.current.forEach(topic => {
                client.unsubscribe(topic);
            });
            subscribedTopics.current.clear();
        };
    }, [client, deviceIds, topicBase, includeGraphTopic, projectId]);

    // Función helper para obtener variable actualizada por su ID
    const getVariableById = useCallback((variableId) => {
        for (const deviceId in data) {
            const variables = data[deviceId];
            if (Array.isArray(variables)) {
                const found = variables.find(v => v.id === variableId);
                if (found) return found;
            }
        }
        return null;
    }, [data]);

    // Función helper para obtener nuevos puntos de gráfico por variable_data_id
    const getGraphPointsByVariableDataId = useCallback((variableDataId) => {
        const points = [];
        for (const deviceId in graphData) {
            const devicePoints = graphData[deviceId] || [];
            const matchingPoints = devicePoints.filter(p => p.variable_data_id === variableDataId);
            points.push(...matchingPoints);
        }
        return points;
    }, [graphData]);

    // Función para limpiar los puntos de gráfico consumidos (para evitar acumulación infinita)
    const clearGraphData = useCallback(() => {
        setGraphData({});
    }, []);

    // Función para limpiar los puntos de variables calculadas
    const clearCustomVarData = useCallback(() => {
        setCustomVarData([]);
    }, []);

    return {
        loaded,
        data,
        graphData,
        customVarData,
        reconnecting,
        getVariableById,
        getGraphPointsByVariableDataId,
        clearGraphData,
        clearCustomVarData
    };
}
